// Firebase — now bundled via npm by Vite (was CDN <script type=module>).
// Public interface is unchanged: this module sets up window.phospheraAuth and
// dispatches the 'phosphera-firebase-ready' event, exactly like before, so the
// rest of the app keeps working without modification.
import { initializeApp } from 'firebase/app';
import {
  getAuth, GoogleAuthProvider, signInWithPopup, signOut,
  onAuthStateChanged, setPersistence, browserLocalPersistence,
  RecaptchaVerifier, signInWithPhoneNumber, linkWithPhoneNumber
} from 'firebase/auth';
import {
  getFirestore, doc, getDoc, getDocFromServer, setDoc, onSnapshot,
  deleteDoc, collection, getDocs, initializeFirestore,
  persistentLocalCache, persistentMultipleTabManager
} from 'firebase/firestore';

const firebaseConfig = {
    apiKey: "AIzaSyAHpF6SxXxzjRJVkzq6MhzdQFRypTJcEQM",
    authDomain: "phosphera-400a6.firebaseapp.com",
    projectId: "phosphera-400a6",
    storageBucket: "phosphera-400a6.firebasestorage.app",
    messagingSenderId: "1014477342117",
    appId: "1:1014477342117:web:85cb63bdb3d82bd887d44c"
  };

  const ALLOWED_EMAILS = [
    "mata55720vova@gmail.com",
    "bomzhixa1@gmail.com"
  ];

  // Phone numbers allowed to sign in (international format, no spaces)
  const ALLOWED_PHONES = [
    "+380956467739",
    "+353879573829"
  ];

  // ─── ADMIN ──────────────────────────────────────────────────
  // Only this email/phone can manage the whitelist via UI.
  // These are HARDCODED for safety — never change at runtime.
  const ADMIN_EMAILS = ["mata55720vova@gmail.com"];
  const ADMIN_PHONES = ["+353879573829"];
  // Hardcoded admin UIDs — always admin regardless of email/phone provider state.
  // Bohdan's primary account.
  const ADMIN_UIDS = ["HK1bqdUIedhcLY0s11hLv3FyMh73"];

  // ─── Google Drive backend (for media attachments) ───
  // Web OAuth client created in Google Cloud Console for phosphera-400a6.
  // Scope: drive.file (per-file access, never sees other user files).
  const DRIVE_CLIENT_ID = '1014477342117-fuuvo7p628j8fqep4vkolbg6b4b98ebf.apps.googleusercontent.com';
  const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
  const DRIVE_FOLDER_NAME = 'phosphera attachments';
  window.PHOSPHERA_DRIVE_CONFIG = { DRIVE_CLIENT_ID, DRIVE_SCOPE, DRIVE_FOLDER_NAME };

  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch(console.warn);
  // ─── Firestore with persistent IndexedDB cache (offline support) ───
  // Notes/changes made while offline are queued and synced automatically
  // when connectivity returns. Multi-tab manager keeps multiple windows safe.
  // Falls back to memory cache if IndexedDB unavailable (private browsing, etc).
  let db;
  try {
    db = initializeFirestore(app, {
      localCache: persistentLocalCache({
        tabManager: persistentMultipleTabManager()
      })
    });
    console.log('[phosphera] Firestore persistent cache enabled (offline support active)');
  } catch (e) {
    console.warn('[phosphera] Firestore persistent cache failed, falling back to memory:', e);
    db = getFirestore(app);
  }
  const provider = new GoogleAuthProvider();

  // ─── Firestore sanitizer: removes `undefined` values which Firestore rejects ───
  // Without this, an entry with `attachments: undefined` causes the entire write to fail.
  function _sanitizeFB(value) {
    if (value === undefined) return null;
    if (value === null) return null;
    if (Array.isArray(value)) {
      return value.filter(v => v !== undefined).map(v => _sanitizeFB(v));
    }
    if (typeof value === 'object' && value.constructor === Object) {
      const cleaned = {};
      for (const k of Object.keys(value)) {
        const v = value[k];
        if (v === undefined) continue;
        cleaned[k] = _sanitizeFB(v);
      }
      return cleaned;
    }
    return value;
  }

  // Aggressive normalization: strip all non-digits, then prepend +
  // "+380 73 364 7107" → "+380733647107"
  // "380733647107" → "+380733647107"
  // "+1 (555) 123-4567" → "+15551234567"
  const normalizePhone = (s) => {
    const digits = (s || '').replace(/\D/g, '');
    if (!digits) return '';
    return '+' + digits;
  };
  // Compare two phone strings ignoring formatting differences
  const phoneEquals = (a, b) => normalizePhone(a) === normalizePhone(b);

  // ─── WHITELIST (dynamic, from Firestore) ───
  // Doc: /admin/whitelist  → { emails: [...], phones: [...] }
  let _cachedWhitelist = null;
  async function fetchWhitelist(forceFresh = false) {
    try {
      const ref = doc(db, "admin", "whitelist");
      const snap = forceFresh ? await getDocFromServer(ref) : await getDoc(ref);
      if (snap.exists()) {
        _cachedWhitelist = snap.data();
        console.log('[phosphera] whitelist loaded:', _cachedWhitelist);
        return _cachedWhitelist;
      } else {
        console.log('[phosphera] whitelist doc does not exist — only hardcoded admins allowed');
        _cachedWhitelist = { emails: [], phones: [] };
        return _cachedWhitelist;
      }
    } catch (e) {
      console.error('[phosphera] whitelist fetch failed:', e.message || e);
    }
    return _cachedWhitelist || { emails: [], phones: [] };
  }
  function isEmailAllowed(email) {
    const e = (email || '').toLowerCase().trim();
    if (ALLOWED_EMAILS.map(x => x.toLowerCase()).includes(e)) return true;
    if (_cachedWhitelist && _cachedWhitelist.emails) {
      if (_cachedWhitelist.emails.map(x => (x || '').toLowerCase().trim()).includes(e)) return true;
    }
    return false;
  }
  function isPhoneAllowed(phone) {
    const target = normalizePhone(phone);
    if (!target) return false;
    if (ALLOWED_PHONES.some(p => normalizePhone(p) === target)) return true;
    if (_cachedWhitelist && _cachedWhitelist.phones) {
      if (_cachedWhitelist.phones.some(p => normalizePhone(p) === target)) return true;
    }
    console.log('[phosphera] phone NOT allowed. Target:', target, 'Whitelist:', (_cachedWhitelist?.phones || []).map(p => normalizePhone(p)));
    return false;
  }
  function isAdmin(user) {
    if (!user) return false;
    // Hardcoded UIDs always trump everything (works even if provider data is missing)
    if (user.uid && ADMIN_UIDS.includes(user.uid)) return true;
    if (user.email && ADMIN_EMAILS.includes(user.email)) return true;
    if (user.phoneNumber && ADMIN_PHONES.includes(user.phoneNumber)) return true;
    if (user.providerData) {
      for (const p of user.providerData) {
        if (p.phoneNumber && ADMIN_PHONES.includes(p.phoneNumber)) return true;
      }
    }
    return false;
  }

  window.phospheraAuth = {
    // Internal helpers exposed for outer scope re-checks
    isAdmin: (user) => isAdmin(user),
    isEmailAllowed: (email) => isEmailAllowed(email),
    isPhoneAllowed: (phone) => isPhoneAllowed(phone),
    fetchWhitelist: (forceFresh) => fetchWhitelist(forceFresh),

    signIn: async () => {
      const result = await signInWithPopup(auth, provider);
      // Always fetch fresh whitelist from server (not cache) before access check
      await fetchWhitelist(true);
      if (!isEmailAllowed(result.user.email)) {
        await signOut(auth);
        alert(`Access denied. This account is not on the allowed list.`);
        return null;
      }
      return result.user;
    },
    signOut: () => {
      try { localStorage.removeItem('phosphera-cached-user'); } catch (e) {}
      return signOut(auth);
    },
    onAuth: (cb) => {
      let firebaseResponded = false;
      let lastUid = null;

      // ─── Offline fast-path ───
      // If we have a cached user from a previous session AND we're offline (or
      // Firebase is slow), invoke the callback immediately so the UI loads.
      // Firebase Auth can hang for 30+ seconds offline waiting for its servers
      // — without this, user gets stuck on the login screen.
      if (!navigator.onLine) {
        try {
          const cached = JSON.parse(localStorage.getItem('phosphera-cached-user') || 'null');
          if (cached && cached.uid) {
            console.log('[phosphera] Offline detected — restoring cached user');
            lastUid = cached.uid;
            cb(cached);
          }
        } catch (e) {}
      } else {
        // We're online but Firebase Auth can still be slow. Set a 3s timeout —
        // if no response by then AND we have a cached user, use the cache.
        setTimeout(() => {
          if (firebaseResponded) return;
          try {
            const cached = JSON.parse(localStorage.getItem('phosphera-cached-user') || 'null');
            if (cached && cached.uid) {
              console.log('[phosphera] Firebase Auth slow — using cached user for instant load');
              lastUid = cached.uid;
              cb(cached);
            }
          } catch (e) {}
        }, 3000);
      }

      // Real Firebase Auth listener
      return onAuthStateChanged(auth, async (user) => {
        firebaseResponded = true;
        if (user) {
          // Cache user info so we can fast-path next time offline
          try {
            const cacheable = {
              uid: user.uid,
              email: user.email || null,
              displayName: user.displayName || null,
              phoneNumber: user.phoneNumber || null,
              providerData: (user.providerData || []).map(p => ({
                providerId: p.providerId, phoneNumber: p.phoneNumber || null
              })),
            };
            localStorage.setItem('phosphera-cached-user', JSON.stringify(cacheable));
          } catch (e) {}
          try { await fetchWhitelist(); } catch (e) { console.warn('[phosphera] fetchWhitelist failed (likely offline):', e); }
          // Skip duplicate callbacks for the same user
          if (lastUid === user.uid) return;
          lastUid = user.uid;
        } else {
          // Real sign-out → clear cache
          try { localStorage.removeItem('phosphera-cached-user'); } catch (e) {}
          lastUid = null;
        }
        cb(user);
      });
    },
    // ─── LEGACY DATA API (kept for migration path) ───
    saveData: async (uid, data) => setDoc(doc(db, "users", uid, "data", "main"), { data }),
    loadData: async (uid) => {
      const snap = await getDoc(doc(db, "users", uid, "data", "main"));
      return snap.exists() ? snap.data().data : null;
    },
    watchData: (uid, cb) => onSnapshot(doc(db, "users", uid, "data", "main"), (snap) => {
      if (snap.exists()) cb(snap.data().data);
    }),

    // ─── NEW SCHEMA v2: meta + per-section docs ───
    // /users/{uid}/meta/main → settings (_tileSettings, _bookmarks, _financeSettings...)
    // /users/{uid}/sections/{slug} → { entries, subtopics, links, sortMode, activeSubtopic }
    saveMeta: async (uid, meta) => setDoc(doc(db, "users", uid, "meta", "main"), _sanitizeFB(meta)),
    loadMeta: async (uid) => {
      const snap = await getDoc(doc(db, "users", uid, "meta", "main"));
      return snap.exists() ? snap.data() : null;
    },
    watchMeta: (uid, cb) => onSnapshot(doc(db, "users", uid, "meta", "main"), (snap) => {
      if (snap.exists()) cb(snap.data());
    }),
    saveSection: async (uid, slug, sectionData) =>
      setDoc(doc(db, "users", uid, "sections", slug), _sanitizeFB(sectionData)),
    loadSection: async (uid, slug) => {
      const snap = await getDoc(doc(db, "users", uid, "sections", slug));
      return snap.exists() ? snap.data() : null;
    },
    deleteSection: async (uid, slug) => deleteDoc(doc(db, "users", uid, "sections", slug)),
    watchSection: (uid, slug, cb) =>
      onSnapshot(doc(db, "users", uid, "sections", slug), (snap) => {
        if (snap.exists()) cb(snap.data());
        else cb(null);
      }),
    listSectionSlugs: async (uid) => {
      const snap = await getDocs(collection(db, "users", uid, "sections"));
      return snap.docs.map(d => d.id);
    },

    signInWithPhone: async (phoneNumber, recaptchaContainerId) => {
      const phone = normalizePhone(phoneNumber);
      console.log('[phosphera] Phone sign-in start. Phone:', phone);
      await fetchWhitelist(true);
      if (!isPhoneAllowed(phone)) {
        throw new Error(`This phone number is not on the allowed list.`);
      }
      const container = document.getElementById(recaptchaContainerId);
      if (container) container.innerHTML = '';
      console.log('[phosphera] Creating reCAPTCHA verifier...');
      const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'normal' });
      try {
        const widgetId = await verifier.render();
        console.log('[phosphera] reCAPTCHA rendered, widget id:', widgetId);
      } catch (e) {
        console.error('[phosphera] reCAPTCHA render failed:', e.code || '', e.message || e);
        throw new Error('reCAPTCHA failed: ' + (e.message || e));
      }
      console.log('[phosphera] Sending verification code...');
      try {
        const result = await signInWithPhoneNumber(auth, phone, verifier);
        console.log('[phosphera] SMS sent successfully');
        return result;
      } catch (e) {
        console.error('[phosphera] signInWithPhoneNumber failed:', e.code || '', e.message || e, e);
        throw e;
      }
    },

    linkPhone: async (phoneNumber, recaptchaContainerId) => {
      if (!auth.currentUser) {
        throw new Error("You must be signed in first to link a phone.");
      }
      const phone = normalizePhone(phoneNumber);
      await fetchWhitelist(true);
      if (!isPhoneAllowed(phone)) {
        throw new Error(`Phone ${phone} is not on the allowed list.`);
      }
      const container = document.getElementById(recaptchaContainerId);
      if (container) container.innerHTML = '';
      const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'normal' });
      try {
        await verifier.render();
      } catch (e) {
        console.error('[phosphera] reCAPTCHA render failed:', e);
        throw new Error('reCAPTCHA failed: ' + (e.message || e));
      }
      return await linkWithPhoneNumber(auth.currentUser, phone, verifier);
    },

    // ─── ATTACHMENTS ───
    saveAttachment: async (uid, attachmentId, attachmentData) => {
      return setDoc(doc(db, "users", uid, "attachments", attachmentId), _sanitizeFB(attachmentData));
    },
    loadAttachment: async (uid, attachmentId) => {
      const snap = await getDoc(doc(db, "users", uid, "attachments", attachmentId));
      return snap.exists() ? snap.data() : null;
    },
    deleteAttachment: async (uid, attachmentId) => {
      return deleteDoc(doc(db, "users", uid, "attachments", attachmentId));
    },

    // ─── ADMIN ───
    isAdmin: () => isAdmin(auth.currentUser),
    getWhitelist: async () => {
      await fetchWhitelist();
      return _cachedWhitelist || { emails: [], phones: [] };
    },
    saveWhitelist: async (whitelist) => {
      if (!isAdmin(auth.currentUser)) {
        throw new Error("Only admins can modify the whitelist.");
      }
      const data = {
        emails: Array.isArray(whitelist.emails) ? whitelist.emails : [],
        phones: Array.isArray(whitelist.phones) ? whitelist.phones : [],
      };
      console.log('[phosphera] Saving whitelist:', data);
      try {
        await setDoc(doc(db, "admin", "whitelist"), data);
        console.log('[phosphera] Whitelist saved successfully');
      } catch (e) {
        console.error('[phosphera] Whitelist save FAILED:', e.code || '', e.message || e);
        throw e;
      }
      // Force re-read from server (NOT cache) so we know the doc actually persisted
      const verify = await fetchWhitelist(true);
      console.log('[phosphera] Server now has:', verify);
      return data;
    }
  };
  window.phospheraReady = true;
  window.dispatchEvent(new Event('phosphera-firebase-ready'));
